import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { FilterOption, FilterType, FilterValues } from "../api/types";
import { useLocale } from "../hooks/useLocale";
import styles from "./ReportFilters.module.css";

export interface RuntimeFilter {
  id: string;
  label: string;
  type: FilterType;
}

interface RuntimeFiltersProps {
  layout?: "inline" | "panel";
  optionsScope: string;
  filters: RuntimeFilter[];
  values: FilterValues;
  onChange: (filterId: string, values: string[]) => void;
  loadOptions: (filterId: string) => Promise<FilterOption[]>;
}

// Shared config-driven filter controls. Reports and dashboards own their
// selection state and options endpoint; this component owns only interaction.
export function RuntimeFilters({
  layout = "inline",
  optionsScope,
  filters,
  values,
  onChange,
  loadOptions,
}: RuntimeFiltersProps) {
  return (
    <>
      {filters.map((filter) =>
        filter.type === "daterange" ? (
          <DateRangeFilter
            layout={layout}
            key={`${optionsScope}:${filter.id}`}
            filter={filter}
            value={values[filter.id] ?? []}
            onChange={(next) => onChange(filter.id, next)}
          />
        ) : (
          <MultiSelectFilter
            layout={layout}
            key={`${optionsScope}:${filter.id}`}
            filter={filter}
            value={values[filter.id] ?? []}
            onChange={(next) => onChange(filter.id, next)}
            loadOptions={() => loadOptions(filter.id)}
            single={filter.type === "singleselect"}
          />
        ),
      )}
    </>
  );
}

interface MultiSelectFilterProps {
  layout: "inline" | "panel";
  filter: RuntimeFilter;
  value: string[];
  onChange: (values: string[]) => void;
  loadOptions: () => Promise<FilterOption[]>;
  single: boolean;
}

function MultiSelectFilter({
  layout,
  filter,
  value,
  onChange,
  loadOptions,
  single,
}: MultiSelectFilterProps) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<FilterOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const defaultEmitted = useRef(false);
  const radioGroupName = useId();

  // Multiselect options stay lazy. Singleselect options are loaded immediately
  // because their first row is the active default.
  const fetchOptions = useCallback(async () => {
    if (options !== null || loading) return;
    setLoading(true);
    setError(false);
    try {
      setOptions(await loadOptions());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [options, loading, loadOptions]);

  useEffect(() => {
    if (open || single) void fetchOptions();
  }, [open, single, fetchOptions]);

  useEffect(() => {
    if (
      single &&
      !defaultEmitted.current &&
      value.length === 0 &&
      options !== null &&
      options.length > 0
    ) {
      defaultEmitted.current = true;
      onChange([options[0].value]);
    }
  }, [single, value.length, options, onChange]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggleValue(nextValue: string) {
    if (single) {
      onChange([nextValue]);
      setOpen(false);
      return;
    }
    onChange(
      value.includes(nextValue)
        ? value.filter((item) => item !== nextValue)
        : [...value, nextValue],
    );
  }

  const selectedOption = options?.find((option) => option.value === value[0]);
  const summary = single
    ? selectedOption?.label ?? value[0] ?? t("report.filter_loading")
    : value.length === 0
      ? t("report.filter_all")
      : t("report.filter_selected", { count: value.length });
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = (options ?? []).filter(
    (option) =>
      !normalizedQuery || option.label.toLowerCase().includes(normalizedQuery),
  );

  return (
    <div className={`${styles.filter} ${layout === "panel" ? styles.panelFilter : ""}`} ref={rootRef}>
      <span className={styles.label} id={`${radioGroupName}-label`}>{filter.label}</span>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-labelledby={`${radioGroupName}-label ${radioGroupName}-summary`}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={styles.summary} id={`${radioGroupName}-summary`}>{summary}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className={styles.dropdown} role="listbox">
          {(options?.length ?? 0) > 8 && (
            <input
              type="text"
              className={styles.search}
              placeholder={t("report.filter_search")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
            />
          )}
          <div className={styles.optionList}>
            {loading && (
              <p className={styles.hint}>{t("report.filter_loading")}</p>
            )}
            {error && (
              <p className={styles.hint}>
                {t("report.filter_options_failed")}
              </p>
            )}
            {!loading &&
              !error &&
              visibleOptions.length === 0 &&
              options !== null && (
                <p className={styles.hint}>{t("report.filter_empty")}</p>
              )}
            {!loading &&
              !error &&
              visibleOptions.map((option) => (
                <label key={option.value} className={styles.option}>
                  <input
                    type={single ? "radio" : "checkbox"}
                    name={single ? radioGroupName : undefined}
                    checked={value.includes(option.value)}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
          </div>
          {!single && value.length > 0 && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => onChange([])}
            >
              {t("report.filter_clear")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface DateRangeFilterProps {
  layout: "inline" | "panel";
  filter: RuntimeFilter;
  value: string[];
  onChange: (values: string[]) => void;
}

function DateRangeFilter({ filter, value, onChange, layout }: DateRangeFilterProps) {
  const { t } = useLocale();
  const [from, setFrom] = useState(value[0] ?? "");
  const [to, setTo] = useState(value[1] ?? "");
  const lastEmitted = useRef<string[]>(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    setFrom(value[0] ?? "");
    setTo(value[1] ?? "");
  }, [value]);

  function emit(nextFrom: string, nextTo: string) {
    const next = nextFrom && nextTo ? [nextFrom, nextTo] : [];
    lastEmitted.current = next;
    onChange(next);
  }

  return (
    <div className={`${styles.filter} ${layout === "panel" ? styles.panelFilter : ""}`}>
      <span className={styles.label}>{filter.label}</span>
      <div className={styles.dateRange}>
        <label className={styles.dateField}>
          <span>{t("date.from")}</span>
          <input
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              emit(event.target.value, to);
            }}
          />
        </label>
        <label className={styles.dateField}>
          <span>{t("date.to")}</span>
          <input
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              emit(from, event.target.value);
            }}
          />
        </label>
      </div>
    </div>
  );
}
